import React, { useCallback, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormikInput, Select, Button } from '../../../../components/UI';
import Loader from '../../../../components/UI/Loader/Loader';
import { addGithubIntegration, getAllGithubBranches, getSpecificGithubIntegration, updateSpecificGithubIntegration, validateGithubIntegrationPathAndToken } from '../../../../utils/APIs';
import backwardArrow from '../../../../assets/icons/backward-arrow.svg';
import { NewGithubIntegrationSchema } from '../../../../schemas';
import { useContentfulContext } from '../../../../contexts/ContentfulContext';
import { useUserContext } from '../../../../contexts/UserContext';
import { Eye, EyeOff } from 'lucide-react';
import { debounce } from '@mui/material';

const AddEditGithubIntegration = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userData, updateGithubIntegrations } = useUserContext();
  const { content } = useContentfulContext();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();

  const [isValidated, setIsValidated] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [validationSuccess, setValidationSuccess] = useState("");
  const [branches, setBranches] = useState([]);
  const [isFetchingBranches, setIsFetchingBranches] = useState(false);
  const [showToken, setShowToken] = useState(false);

  const [branchFetchParams, setBranchFetchParams] = useState({
    repo: data?.repo_name || "",
    owner: data?.repo_owner_username || "",
    token: data?.access_token || ""
  });

  // File extension options
  const fileExtensionOptions = ["js", "json", "html"];
  const dropdownItems = fileExtensionOptions.map(ext => ({ name: ext, val: ext }));

  useEffect(() => {
    fetchIntegrationData();
  }, [id, userData]);

  const resetValidationState = () => {
    setIsValidated(false);
    setValidationSuccess("");
    setValidationError("");
  };


  const handleValidate = async (values, { setSubmitting, setTouched, validateForm }) => {
    try {
      setIsValidating(true);
      setValidationError("");

      // Trigger all field validations by setting all fields as touched
      const allFields = Object.keys(values);
      const touchedFields = allFields.reduce((acc, field) => {
        acc[field] = true;
        return acc;
      }, {});
      setTouched(touchedFields);

      // Validate the form
      const errors = await validateForm(values);

      // If there are validation errors, stop here
      if (Object.keys(errors).length > 0) {
        toast.error("Please fix the form errors before validating.");
        setSubmitting(false);
        return;
      }

      const payload = {
        access_token: values.access_token,
        repo_name: values.repo_name,
        repo_owner_username: values.repo_owner_username,
        branch_name: values.branch_name,
        include_paths: values.include_paths, // string or comma-sep
      };

      const result = await validateGithubIntegrationPathAndToken(
        userData.token,
        userData.selectedOrganization.org_token,
        payload
      );

      const invalidPaths = result?.data?.invalid || [];

      if (invalidPaths.length === 0) {
        setIsValidated(true);
        setValidationSuccess("All paths are valid.");
      } else {
        setIsValidated(false);
        setValidationError(
          `Invalid paths: ${invalidPaths.map(p => p.path).join(", ")}`
        );
      }
    } catch (error) {
      setIsValidated(false);
      setValidationError(
        error?.response?.message || "Validation failed. Please check your inputs and try again."
      );
    } finally {
      setIsValidating(false);
    }
  };

  const fetchIntegrationData = async () => {
    try {
      setLoading(true);
      if (id && userData?.selectedOrganization?.org_token) {
        const result = await getSpecificGithubIntegration(
          userData.token,
          userData.selectedOrganization.org_token,
          id
        );
        if (result.status) {
          const fetchedData = result?.data;
          setData(fetchedData);

          const { repo_name, repo_owner_username, access_token } = fetchedData;
          setBranchFetchParams({ repo: repo_name, owner: repo_owner_username, token: access_token });

          // Fetch branches after fetching integration
          debouncedFetchBranches(repo_name, repo_owner_username, access_token);

        }
      }
    } catch (error) {
      console.error("Error fetching integration data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced version of fetchBranches
  const debouncedFetchBranches = useCallback(
    debounce(async (repo, owner, token) => {
      if (!repo || !owner || !token) {
        setBranches([]);
        return;
      }
      try {
        setIsFetchingBranches(true);
        const res = await getAllGithubBranches({
          token: userData.token,
          organizationId: userData.selectedOrganization.org_token,
          data: { access_token: token, repo_name: repo, repo_owner_username: owner }
        });
        setBranches(res?.data || []);
      } catch (err) {
        console.error("Error fetching branches:", err);
        setBranches([]);
      } finally {
        setIsFetchingBranches(false);
      }
    }, 700), // 700ms debounce
    [userData]
  );

  useEffect(() => {
    const { repo, owner, token } = branchFetchParams;

    if (repo && owner && token) {
      debouncedFetchBranches(repo, owner, token);
    } else {
      setBranches([]);
    }

    // Cleanup debounce on unmount
    return () => debouncedFetchBranches.clear?.();
  }, [branchFetchParams, debouncedFetchBranches]);

  const handleSubmit = async (values, actions) => {
    try {
      setLoading(true);
      let result;
      const rawPayload = {
        ...values,
        file_extensions: values.file_extensions.join(','), // convert array to comma string
      };

      const payload = rawPayload;

      if (id) {
        result = await updateSpecificGithubIntegration(
          userData.token,
          userData.selectedOrganization.org_token,
          { integrationId: id, bodyData: payload }
        );
      } else {
        result = await addGithubIntegration(userData.token, {
          organizationId: userData.selectedOrganization.org_token,
          data: payload,
        });
      }

      if (result.status) {
        toast.success(result?.message || content.GITHUB_CONTENT.success?.update);
        updateGithubIntegrations();
      }

      setTimeout(() => {
        navigate('/setup/github-integrations');
      }, 1000);
    } catch (error) {
      toast.error(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-4 md:px-8 my-6">
      {loading && <Loader />}
      <div className="flex flex-col lg:w-1/2 justify-center items-start">
        <div className="space-y-4 md:space-y-6 w-full">
          <p className="font-medium cursor-pointer text-primary hover:underline flex items-center" onClick={() => navigate('/setup/github-integrations')}>
            <img src={backwardArrow} alt="" className="w-5 h-5 mr-2" /> {"Back to Github Integrations"}
          </p>

          <Formik
            initialValues={{
              name: data?.name || "",
              access_token: data?.access_token || "",
              repo_name: data?.repo_name || "",
              repo_owner_username: data?.repo_owner_username || "",
              branch_name: data?.branch_name || "",
              file_extensions: data?.file_extensions || [],
              include_paths: data?.include_paths?.join(',') || "",
              naming_convention: data?.naming_convention || ""
            }}
            enableReinitialize
            validationSchema={() => NewGithubIntegrationSchema(content.NEW_CONTENTFUL_CONTENT)}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, errors, touched, setTouched, setSubmitting, validateForm }) => (
              <Form action="#" className="space-y-4">
                <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">{"Github Integration"}</h2>
                <p>{"Add or edit a Github integration for your organisation."}</p>

                {/* Name */}
                <FormikInput
                  type="text"
                  id="name"
                  name="name"
                  label="Name"
                  mandatory
                  helperText="Help text"
                />

                {/* Access Token */}
                <FormikInput
                  type={showToken ? "text" : "password"}
                  id="access_token"
                  name="access_token"
                  label="Access Token"
                  mandatory
                  helperText="Enter the Access Token provided by Github"
                  onChange={(e) => {
                    resetValidationState();
                    setBranchFetchParams(prev => ({ ...prev, token: e.target.value }));
                  }}
                  suffixIcon={
                    <button
                      type="button"
                      onClick={() => setShowToken(prev => !prev)}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showToken ? "Hide access token" : "Show access token"}
                    >
                      {showToken ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />

                {/* repo_name */}
                <FormikInput
                  type="text"
                  id="repo_name"
                  name="repo_name"
                  label="Repository Name"
                  mandatory
                  helperText="Enter Repository name"
                  onChange={(e) => {
                    resetValidationState();
                    setBranchFetchParams(prev => ({ ...prev, repo: e.target.value }));
                  }}
                />

                {/* repo_owner_username */}
                <FormikInput
                  type="text"
                  id="repo_owner_username"
                  name="repo_owner_username"
                  label="Repository Owner Github Username"
                  mandatory
                  helperText="Enter the username of repository owner from github"
                  onChange={(e) => {
                    resetValidationState();
                    setBranchFetchParams(prev => ({ ...prev, owner: e.target.value }));
                  }}
                />

                {/* Branch Name (Select) */}
                <div className="relative w-full">
                  <Select
                    id="branch_name"
                    name="branch_name"
                    label="Branch Name"
                    mandatory
                    helperText={isFetchingBranches ? "Fetching branches..." : "Select a branch from the repository"}
                    options={branches?.map(b => ({ value: b, label: b })) || []}
                    value={values.branch_name}
                    placeholder={isFetchingBranches ? "Fetching Branches..." : "Select Branch"}
                    error={touched.branch_name && errors.branch_name ? errors.branch_name : ""}
                    disabled={
                      isFetchingBranches ||
                      !values.repo_name ||
                      !values.repo_owner_username ||
                      !values.access_token ||
                      branches.length === 0
                    }
                    onChange={(val) => {
                      const selectedVal = val?.target ? val.target.value : val;
                      setFieldValue("branch_name", selectedVal || "");
                      resetValidationState();
                    }}
                  />
                </div>

                {/* File Extensions (Multi-select) */}
                <div className="relative w-full">
                  <Select
                    id="file_extensions"
                    name="file_extensions"
                    label="File Extensions"
                    mandatory
                    helperText="Select the extensions to use for this integration"
                    options={dropdownItems.map(item => ({ value: item.val, label: item.name }))}
                    multiSelect={true}
                    value={values.file_extensions || []}
                    placeholder="Select File Extensions"
                    error={touched.file_extensions && errors.file_extensions ? errors.file_extensions : ""}
                    onChange={(selected) => {
                      setFieldValue("file_extensions", Array.isArray(selected) ? selected : [selected]);
                      setTouched({ ...touched, selected_offer: true });
                      resetValidationState();
                    }}
                  />
                </div>

                {/* Include Paths */}
                <FormikInput
                  type="text"
                  id="include_paths"
                  name="include_paths"
                  label="Include Paths"
                  helperText="Enter the comma separated include paths"
                  onChange={() => resetValidationState()}
                />

                {/* Naming Convention */}
                <FormikInput
                  type="text"
                  id="naming_convention"
                  name="naming_convention"
                  label="Naming Convention"
                  helperText="Enter the naming convention regex"
                  onChange={() => resetValidationState()}
                />

                {validationSuccess && (
                  <div className="p-2 text-green-600">✓ {validationSuccess}</div>
                )}
                {validationError && (
                  <div className="p-2 text-red-600">✗ {validationError}</div>
                )}

                {/* Submit Button */}
                <div className="flex justify-start items-center gap-2 pt-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => handleValidate(values, { setSubmitting, setTouched, validateForm })}
                    disabled={isValidating || isValidated}
                  >
                    {isValidating ? "Validating..." : isValidated ? "Validated" : "Validate"}
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!isValidated || loading}
                    className={!isValidated ? "opacity-50 cursor-not-allowed" : ""}
                  >
                    {loading ? "Saving..." : (id ? "Update Integration" : "Create Integration")}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default AddEditGithubIntegration;
